import { modelOptions, post, prop } from "@typegoose/typegoose";
import { getZodSchemaFromClass, SproutCheckTypeString, validate } from "../../../zod-decorator/index.js";
import type { SproutCheckType } from "../../../zod-decorator/index.js";
import { z } from "zod";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type {
    VerificationCheckConfigurationFieldsWithDefaults,
    VerificationCheckConfigurationSchemaTypes,
} from "../../types/verificationCheckConfiguration.d.js";
import { type DtoValidatorTypes } from "../../validatableClass.js";

const SCHEMA_KEY = "VerificationCheckConfiguration" as const;

/**
 * Base configuration for a verification.
 *
 * This class represents the configuration for a specific verification type,
 * including whether it is enabled or not.
 *
 * @class VerificationCheckConfiguration
 * @property {SproutCheckType} verificationCheckType - The type of verification.
 * @property {boolean} enabled - Whether this verification is enabled.
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<VerificationCheckConfiguration>>(...addSchemaKeyHook(SCHEMA_KEY))
export class VerificationCheckConfiguration extends SchemaBase<
    VerificationCheckConfiguration,
    VerificationCheckConfigurationFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: SproutCheckTypeString })
    @prop({ type: () => String, required: true })
    public verificationCheckType!: SproutCheckType;

    @validate({ zodValidator: z.boolean().optional() })
    @prop({ type: () => Boolean, required: true, default: () => true })
    public enabled: boolean = true;

    static getters = {} as const satisfies Record<string, (self: VerificationCheckConfiguration) => any>;
    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<VerificationCheckConfigurationSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<
            VerificationCheckConfiguration,
            VerificationCheckConfigurationFieldsWithDefaults
        >,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

VerificationCheckConfiguration.dtoValidators = getZodSchemaFromClass(
    VerificationCheckConfiguration,
) as unknown as DtoValidatorTypes<VerificationCheckConfigurationSchemaTypes>;
